import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Campaign = () => {
    const { appId, language, userId } = useAppId();
    const navigate = useNavigate();

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            title: "Campaign List",
            loading: "Loading Campaigns...",
            noData: "No campaigns found.",
            sent: "Sent",
            cost: "Cost",
            sms: "SMS"
        },
        hi: {
            title: "कैंपेन सूची",
            loading: "कैंपेन लोड हो रहे हैं...",
            noData: "कोई कैंपेन नहीं मिला।",
            sent: "भेजे गए",
            cost: "खर्च",
            sms: "SMS"
        },
        gu: {
            title: "કેમ્પેઇન",
            loading: "કેમ્પેઇન લોડ થઈ રહ્યા છે...",
            noData: "કોઈ કેમ્પેઇન મળ્યું નથી.",
            sent: "મોકલેલ",
            cost: "ખર્ચ",
            sms: "SMS"
        }
    };

    const txt = t[lang] || t.en;

    // --- Fetch Campaign List Data ---
    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchCampaigns = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_wtsp_campaign_list`, {
                    app_id: appId,
                    user_id: userId || "1"
                });

                if (response.data.success && response.data.data && response.data.data.result) {
                    setCampaigns(response.data.data.result);
                }
            } catch (error) {
                console.error("Error fetching campaign list:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaigns();
    }, [appId, userId, navigate]);

    return (
        <div className="campaign-page-wrapper">
            <Header title={txt.title} />

            <div className="campaign-container">
                {loading && (
                    <div className="loader-wrapper">
                        <div className="spinner"></div>
                        <p className="loader-text">{txt.loading}</p>
                    </div>
                )}

                {!loading && campaigns.length === 0 && (
                    <div className="empty-state">
                        <p>{txt.noData}</p>
                    </div>
                )}

                {!loading && campaigns.length > 0 && (
                    <div className="campaign-list mt-4">
                        {campaigns.map((camp, index) => (
                            <div className="camp-card" key={camp.id || index} onClick={()=>{navigate('/dis_meta_bulk_campaign_wise_voter_list',{state:{campaign:camp,campaign_id:camp.campaign_id}})}}>
                                
                                {/* Top Section: Icon, ID, and Date */}
                                <div className="camp-header">
                                    <div className="camp-icon-box">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                            <polyline points="14 2 14 8 20 8"></polyline>
                                            <line x1="16" y1="13" x2="8" y2="13"></line>
                                            <line x1="16" y1="17" x2="8" y2="17"></line>
                                            <polyline points="10 9 9 9 8 9"></polyline>
                                        </svg>
                                    </div>
                                    <div className="camp-info">
                                        <div className="camp-id">{camp.campaign_id}</div>
                                        <div className="camp-date">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            {camp.create_date}
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className="camp-divider"></div>

                                {/* Bottom Section: Stats */}
                                <div className="camp-stats">
                                    {/* Left Stat: SMS Count */}
                                    <div className="stat-col">
                                        <div className="stat-value">
                                            <span className="stat-icon sms-icon">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                                                </svg>
                                            </span>
                                            {camp.total_message_use} {txt.sms}
                                        </div>
                                        <div className="stat-label">{txt.sent}</div>
                                    </div>

                                    {/* Right Stat: Cost */}
                                    <div className="stat-col">
                                        <div className="stat-value">
                                            <span className="stat-icon cost-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.64-2.25 1.64-1.74 0-2.33-.89-2.46-1.87H7.76c.15 1.78 1.48 2.82 3.14 3.14V19h1.86v-1.7c1.61-.31 2.84-1.39 2.84-2.96 0-2.3-1.89-2.9-3.29-3.2z"/>
                                                </svg>
                                            </span>
                                            ₹ {Number(camp.total_cost || 0).toFixed(2)}
                                        </div>
                                        <div className="stat-label">{txt.cost}</div>
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style jsx>{`
                /* Background matching the image (Light Lavender/Blue) */
                 .header-container-white {
                    background-color: #ffffff;
                    padding: 15px 32px;
                    border-bottom: 1px solid #e2e8f0;
                    margin: -24px 0px 10px 0px; 
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                .campaign-page-wrapper {
                    background-color: #E6ECF5;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    width: 100%;
                }

                .campaign-container {
                    padding: 16px;
                    width: 100%;
                    max-width: 100%;
                    margin: 0;
                }

                /* Loader */
                .loader-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    min-height: 50vh;
                }
                .spinner {
                    width: 40px; height: 40px;
                    border: 4px solid #d1d5db;
                    border-top: 4px solid #10b981;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 10px;
                }
                .loader-text { color: #6b7280; font-weight: 600; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                .empty-state {
                    text-align: center;
                    color: #64748b;
                    padding: 40px 0;
                    font-weight: 500;
                }

                /* List & Cards */
                .campaign-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
                    gap: 16px;
                    padding-bottom: 20px;
                    width: 100%;
                }

                .camp-card {
                    background: #F9FBFC;
                    border-radius: 20px;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
                    width: 100%;
                }

                /* Top Header Area */
                .camp-header {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .camp-icon-box {
                    width: 52px;
                    height: 52px;
                    background-color: #E0EFEB;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #11816A;
                    flex-shrink: 0;
                }

                .camp-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    flex: 1;
                }

                .camp-id {
                    font-size: 15.5px;
                    font-weight: 800;
                    color: #1A1A1A;
                    letter-spacing: 0.2px;
                }

                .camp-date {
                    font-size: 12.5px;
                    color: #8C95A1;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Horizontal Divider */
                .camp-divider {
                    height: 1px;
                    background-color: #E8EEF4;
                    width: 100%;
                }

                /* Bottom Stats Area */
                .camp-stats {
                    display: flex;
                    justify-content: space-around;
                    align-items: center;
                    padding: 0 10px;
                }

                .stat-col {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    width: 50%;
                }

                .stat-value {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 15px;
                    font-weight: 800;
                    color: #111827;
                }

                .stat-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .sms-icon { color: #11816A; }
                .cost-icon { color: #11816A; }

                .stat-label {
                    font-size: 12.5px;
                    color: #9CA3AF;
                    font-weight: 600;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .campaign-list {
                        grid-template-columns: 1fr;
                    }
                    .campaign-container {
                        padding: 12px;
                    }
                }

                @media (max-width: 480px) {
                    .camp-card {
                        padding: 14px;
                    }
                    .camp-icon-box {
                        width: 44px;
                        height: 44px;
                    }
                    .camp-stats {
                        padding: 0 4px;
                    }
                    .stat-value {
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Campaign;